import { useState } from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LiveClockPanel from '@/components/LiveClockPanel';
import LiveJudgementBar, { type LiveJudgementChoice } from '@/components/live/LiveJudgementBar';
import LiveEventSummaryPanel from '@/components/live/LiveEventSummaryPanel';
import LiveEventVerticalTimeline from '@/components/live/LiveEventVerticalTimeline';
import type { LiveSession, SessionEvent } from '@/types';

type RecordingTab = 'timeline' | 'summary';

interface Props {
  session: LiveSession;
  currentSeconds: number;
  eventCount: number;
  events: SessionEvent[];
  canCapture: boolean;
  canEditEvents?: boolean;
  onJudgementSelect: (value: LiveJudgementChoice) => void;
  onClockAction: (action: string, extra?: { adjust_seconds?: number; period?: string; seconds?: number }) => void;
  onEditEvent?: (event: SessionEvent) => void;
  onDeleteEvent?: (event: SessionEvent) => void;
  onClose: () => void;
}

export default function LiveRecordingMode({
  session,
  currentSeconds,
  eventCount,
  events,
  canCapture,
  canEditEvents,
  onJudgementSelect,
  onClockAction,
  onEditEvent,
  onDeleteEvent,
  onClose,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState<RecordingTab>('timeline');
  const [newestFirst, setNewestFirst] = useState(true);

  const timeline = (
    <LiveEventVerticalTimeline
      events={events}
      canEdit={canEditEvents}
      newestFirst={newestFirst}
      inverted
      onEdit={onEditEvent}
      onDelete={onDeleteEvent}
    />
  );

  const summary = <LiveEventSummaryPanel events={events} inverted />;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (t) => t.zIndex.modal + 10,
        bgcolor: '#0f1419',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          bgcolor: '#151b22',
          flexShrink: 0,
        }}
      >
        <IconButton onClick={onClose} aria-label="退出全屏" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flexShrink: 0,
          position: 'relative',
          zIndex: 5,
          px: { xs: 1, sm: 2 },
          py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          bgcolor: '#121820',
          overflow: 'visible',
        }}
      >
        <LiveClockPanel
          session={session}
          currentSeconds={currentSeconds}
          onClockAction={onClockAction}
          disabled={!session.can_edit}
          compact
          inverted
          showMatchHeader
          eventCount={eventCount}
        />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.25, sm: 2 },
          py: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {canCapture ? (
          <LiveJudgementBar onSelect={onJudgementSelect} inverted />
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', textAlign: 'center', py: 2 }}>
            比賽已結束或無編輯權限
          </Typography>
        )}
      </Box>

      {isMobile && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            flexShrink: 0,
            minHeight: 40,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            bgcolor: '#121820',
            '& .MuiTab-root': { color: 'rgba(255,255,255,0.55)', minHeight: 40, py: 0.75 },
            '& .Mui-selected': { color: '#fff' },
            '& .MuiTabs-indicator': { bgcolor: 'secondary.main' },
          }}
        >
          <Tab value="timeline" label={`時間軸 (${events.length})`} />
          <Tab value="summary" label="統計摘要" />
        </Tabs>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: isMobile ? 'auto' : 'hidden',
          overflowX: 'hidden',
          px: { xs: 1.25, sm: 2 },
          py: 1.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {isMobile ? (
          tab === 'timeline' ? (
            <>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={newestFirst}
                      onChange={(e) => setNewestFirst(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'secondary.main' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: 'secondary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      最新優先
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Stack>
              {timeline}
            </>
          ) : (
            summary
          )
        ) : (
          <Grid container spacing={2} sx={{ height: '100%', minHeight: 0 }}>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: { md: 1 },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  時間軸 ({events.length})
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={newestFirst}
                      onChange={(e) => setNewestFirst(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'secondary.main' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: 'secondary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      最新優先
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Stack>
              {timeline}
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                pl: { md: 1 },
                borderLeft: { md: '1px solid rgba(255,255,255,0.1)' },
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={800}
                sx={{ color: 'rgba(255,255,255,0.85)', mb: 1, flexShrink: 0 }}
              >
                統計摘要
              </Typography>
              <Box sx={{ minWidth: 0, flex: 1 }}>{summary}</Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
}
